
import React from "react";
import { FaPrint } from "react-icons/fa";
import { getHeaderHtml } from "../../../../util/headerUtil";
import { useContext } from "react";
import AuthContext from "../../../../store/auth-context";

// ✅ Admission Print Function
export const printAdmission = (formData, authCtx) => {
  const hospitalData = authCtx?.hospitalData;
  if (/Mobi|Android|iPhone/i.test(navigator.userAgent)) {
    alert("Notice: Printing requires a physical printer connection. Print may not work directly from mobile devices.");
  }

  const consentPart1 =
    "I, the undersigned, hereby voluntarily admit myself/my relative for medical treatment on the advice of the attending doctor. " +
    "I have been informed in detail about my illness, the required investigations, and the treatment advised. " +
    "I fully understand that there may be risks and complications associated with the treatment and that I accept full responsibility for the same.";

  const consentPart2 =
    "I authorize the doctors and staff to carry out the necessary investigations and treatment. " +
    "I also understand that the cost of treatment, medicines, and investigations will be my responsibility " +
    "and that the charges may vary as per requirements. I willingly provide my consent for the treatment.";

  const printWindow = window.open("", "_blank", "width=900,height=650");
  if (!printWindow) return;

  // Data Mappings
  const p = formData.patientId || {};
  const data = {
    patientName: [p.firstName, p.middleName, p.lastName].filter(Boolean).join(" ") || "-",
    date: formData.admissionDateTime ? formData.admissionDateTime.split("T")[0] : "-",
    age: p.age || "-",
    gender: p.gender || "-",
    bloodGroup: formData.bloodGroup || "-",
    mobile: p.mobile || "-",
    ipdNumber: formData.ipdAdmissionId || "-",
    address: p.address || "-",

    floor: formData.wardBed ? formData.wardBed.floor : "-",
    wardInfo: formData.wardBed ? `${formData.wardBed.wardName} - ${formData.wardBed.bedNumber}` : "-",
    referredBy: formData.patientId?.referredBy || p.referredBy || "-",
    reason: formData.admissionReason || "-",
    guardian: formData.guardianName ? `${formData.guardianName} (${formData.guardianRelation})` : "-",
    guardianContact: formData.guardianContactNumber || "-",

    adhar: formData.adharCardNo || "-",
    pan: formData.panCardNo || "-",
    tpa: formData.tpaId ? `${formData.tpaId} / ${formData.tpaValidity?.substring(0, 10)}` : "-",
    insurance: formData.insuranceCardNo || "-",

    symptoms: `${formData.symptomsTitle || "-"} (${formData.option2 || "Type"})`,
    sympDesc: formData.symptomsDesc || "-",
    diagnosis: formData.diagnosis || "-",

    vitals: {
      bp: formData.bp || (formData.bp_systolic || formData.bp_diastolic ? `${formData.bp_systolic || "0"}/${formData.bp_diastolic || "0"}` : "-"),
      pulse: formData.pulse || "-",
      spo2: formData.spo2 || "-",
      temp: formData.temperature || "-",
      weight: formData.weight || "-",
      height: formData.height || "-"
    },
    pho: formData.pho || "-",
    co: formData.co || "-",
    kco: formData.kco || "-",
    cf: formData.cf || "-"
  };


  printWindow.document.write(`
    <html>
      <head>
        <title>Admission Card</title>
        <style>
          @page { size: A4; margin: 10mm; }
          body { font-family: 'Segoe UI', Arial, sans-serif; margin: 20px; color: #333; font-size: 14px; } /* Increased Font */
          
          .header-container { text-align: center; margin-bottom: 20px; }
          .hosp-name { color: #006d77; font-size: 26px; font-weight: bold; margin: 0; text-transform: uppercase; }
          .doc-name { color: #000; font-size: 18px; font-weight: bold; margin: 5px 0; }
          .hosp-details { color: #555; font-size: 14px; margin: 2px 0; }
          .separator { border-top: 1px solid #006d77; margin: 10px 0; }
          
          .title-box { text-align: center; margin: 14px 0; }
          .title-text { font-size: 17px; font-weight: bold; color: #000; text-transform: uppercase; border-bottom: 2px solid #000; padding-bottom: 2px; }

          /* Section Styles */
          .section { margin-bottom: 15px; border: 1px solid #ccc; border-radius: 5px; overflow: hidden; }
          .section-header { background-color: #f0f8fa; padding: 8px 12px; font-weight: bold; font-size: 14px; border-bottom: 1px solid #ccc; color: #000; text-transform: uppercase; }
          .section-body { padding: 12px; }
          
          .grid-row { display: flex; margin-bottom: 8px; }
          .col { flex: 1; padding-right: 15px; min-width: 0; }
          .field-row { display: flex; }
          .label { font-weight: bold; width: 140px; flex-shrink: 0; color: #333; } /* Widened for readability */
          .value { flex: 1; word-wrap: break-word; }
          
          .single-line-ellipsis {
             white-space: nowrap; 
             overflow: hidden; 
             text-overflow: ellipsis; 
             display: block;
          }

          .footer-section { margin-top: 30px; page-break-inside: avoid; border-top: 1px solid #eee; padding-top: 10px;}
          .consent-text { font-size: 12px; text-align: justify; color: #555; margin-bottom: 20px; line-height: 1.4; }
          .signatures { display: flex; justify-content: space-between; margin-top: 40px; font-size: 13px; }
          .sign-block { width: 40%; border-top: 1px solid #000; padding-top: 5px; text-align: center; }

          @media print {
            .footer-section { position: relative; width: 100%; margin-top: 30px; }
            body { margin: 0; padding: 20px; }
          }
        </style>
      </head>
      <body>
        ${hospitalData?.PrescHeader ? `
          ${getHeaderHtml(hospitalData)}
        ` : `<div style="height: 100px;"></div>`}

        <div class="title-box">
          <span class="title-text">ADMISSION CARD</span>
        </div>

        <!-- 1. ADMISSION DETAILS -->
        <div class="section">
          <div class="section-header">Admission Details</div>
          <div class="section-body">
            <div class="grid-row">
              <div class="col"><div class="field-row"><span class="label">Patient Name:</span><span class="value">${data.patientName}</span></div></div>
              <div class="col"><div class="field-row"><span class="label">Adm Date:</span><span class="value">${data.date}</span></div></div>
            </div>
            <div class="grid-row">
              <div class="col"><div class="field-row"><span class="label">Age / Gender:</span><span class="value">${data.age} / ${data.gender}</span></div></div>
              <div class="col"><div class="field-row"><span class="label">Blood Group:</span><span class="value">${data.bloodGroup}</span></div></div>
            </div>
             <div class="grid-row">
              <div class="col"><div class="field-row"><span class="label">Contact:</span><span class="value">${data.mobile}</span></div></div>
              <div class="col"><div class="field-row"><span class="label">IPD No:</span><span class="value">${data.ipdNumber}</span></div></div>
            </div>
             <div class="grid-row">
              <div class="col"><div class="field-row"><span class="label">Address:</span><span class="value">${data.address}</span></div></div>
              <div class="col"><div class="field-row"><span class="label">Ward Details:</span><span class="value">${data.floor} / ${data.wardInfo}</span></div></div>
            </div>
             <div class="grid-row">
              <div class="col"><div class="field-row"><span class="label">Referred By:</span><span class="value">${data.referredBy}</span></div></div>
              <div class="col"><div class="field-row"><span class="label" style="width:140px">Adm Reason:</span><span class="value">${data.reason}</span></div></div>
            </div>
          </div>
        </div>
        
        <!-- 2. DIAGNOSIS DETAILS -->
        <div class="section">
          <div class="section-header">Diagnosis Details</div>
          <div class="section-body">
            <div class="grid-row">
              <div class="col"><div class="field-row"><span class="label">Symptoms:</span><span class="value">${data.symptoms}</span></div></div>
              <div class="col"><div class="field-row"><span class="label">Prov Diagnosis:</span><span class="value">${data.diagnosis}</span></div></div>
            </div>
            <div class="grid-row">
              <div class="col" style="flex:100%"><div class="field-row"><span class="label">Symp Desc:</span><span class="value">${data.sympDesc}</span></div></div>
            </div>
          </div>
        </div>

        <!-- 3. GUARDIAN DETAILS -->
        <div class="section">
          <div class="section-header">Guardian Details</div>
           <div class="section-body">
            <div class="grid-row">
              <div class="col">
                 <div class="field-row">
                   <span class="label">Guardian:</span>
                   <span class="value single-line-ellipsis" title="${data.guardian}">${data.guardian}</span>
                 </div>
              </div>
               <div class="col"><div class="field-row"><span class="label">Contact:</span><span class="value">${data.guardianContact}</span></div></div>
            </div>
          </div>
        </div>
        
        <!-- 4. INSURANCE & PAYMENT DETAILS -->
        <div class="section">
          <div class="section-header">Insurance & Payment Details</div>
           <div class="section-body">
             <div class="grid-row">
              <div class="col"><div class="field-row"><span class="label">Adhar No:</span><span class="value">${data.adhar}</span></div></div>
               <div class="col"><div class="field-row"><span class="label">PAN Card:</span><span class="value">${data.pan}</span></div></div>
            </div>
            <div class="grid-row">
              <div class="col"><div class="field-row"><span class="label">TPA Info:</span><span class="value">${data.tpa}</span></div></div>
               <div class="col"><div class="field-row"><span class="label">Ins Card:</span><span class="value">${data.insurance}</span></div></div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-header">Vital Signs</div>
           <div class="section-body">
            <div class="grid-row" style="font-size: 13px;">
              <div class="col"><strong>BP:</strong> ${data.vitals.bp}</div>
              <div class="col"><strong>Pls:</strong> ${data.vitals.pulse}</div>
              <div class="col"><strong>SpO2:</strong> ${data.vitals.spo2}</div>
              <div class="col"><strong>Temp:</strong> ${data.vitals.temp}</div>
              <div class="col"><strong>Wt:</strong> ${data.vitals.weight}</div>
              <div class="col"><strong>Ht:</strong> ${data.vitals.height}</div>
            </div>
          </div>
        </div>

        <!-- 5. MEDICAL HISTORY & FINDINGS -->
        <div class="section">
          <div class="section-header">Medical History & Findings</div>
          <div class="section-body">
            <div class="grid-row">
              <div class="col"><div class="field-row"><span class="label">PHO:</span><span class="value">${data.pho}</span></div></div>
            </div>
            <div class="grid-row">
              <div class="col"><div class="field-row"><span class="label">CO:</span><span class="value">${data.co}</span></div></div>
            </div>
            <div class="grid-row">
              <div class="col"><div class="field-row"><span class="label">KCO:</span><span class="value">${data.kco}</span></div></div>
            </div>
            <div class="grid-row">
              <div class="col"><div class="field-row"><span class="label">CF:</span><span class="value">${data.cf}</span></div></div>
            </div>
          </div>
        </div>


        <div class="footer-section">
          <div style="font-weight:bold; font-size:14px; margin-bottom:8px; margin-top: 5px;">CONSENT / UNDERTAKING</div>
          <div class="consent-text">
            ${consentPart1} ${consentPart2}
          </div>
          <div class="signatures">
            <div class="sign-block align-left">Patient / Guardian Signature</div>
            <div class="sign-block align-right">Authority Sign / Doctor Sign</div>
          </div>
          ${(() => {
      const userRole = authCtx?.role;
      const userName = authCtx?.userName;
      if (userRole === "ROLE_DOCTOR" && userName) {
        return `<div style="text-align: right; font-size: 11px; margin-top: 2px; font-weight: bold;">( ${userName} )</div>`;
      }
      return "";
    })()}
        </div>

      </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
  printWindow.close();
};

const Print = ({ formData, hospitalData }) => {
  const authCtx = useContext(AuthContext);
  return (
    <button
      onClick={() => printAdmission(formData, authCtx)}
      className="btn-premium"
    >
      <FaPrint className="me-2" /> Print
    </button>
  );
};

export default Print;
